// Thin wrapper around navigator.credentials for Family Repository passkeys.
// The backend (security.py) issues options as base64url; the browser API needs
// ArrayBuffers for `challenge`, `user.id`, and credential `id`s.
import { api } from "@/lib/api";

// ---------- base64url helpers ----------
export function b64uToBuf(b64u) {
    const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
    const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}
export function bufToB64u(buf) {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeRegistrationOptions(opts) {
    const out = { ...opts };
    out.challenge = b64uToBuf(opts.challenge);
    out.user = { ...opts.user, id: b64uToBuf(opts.user.id) };
    if (opts.excludeCredentials) {
        out.excludeCredentials = opts.excludeCredentials.map((c) => ({
            ...c, id: b64uToBuf(c.id),
        }));
    }
    return out;
}
function decodeAuthOptions(opts) {
    const out = { ...opts };
    out.challenge = b64uToBuf(opts.challenge);
    if (opts.allowCredentials) {
        out.allowCredentials = opts.allowCredentials.map((c) => ({
            ...c, id: b64uToBuf(c.id),
        }));
    }
    return out;
}

function encodeRegistrationCredential(cred) {
    const r = cred.response;
    return {
        id: cred.id,
        rawId: bufToB64u(cred.rawId),
        type: cred.type,
        response: {
            attestationObject: bufToB64u(r.attestationObject),
            clientDataJSON: bufToB64u(r.clientDataJSON),
        },
        clientExtensionResults: cred.getClientExtensionResults
            ? cred.getClientExtensionResults()
            : {},
    };
}

function encodeAuthCredential(cred) {
    const r = cred.response;
    return {
        id: cred.id,
        rawId: bufToB64u(cred.rawId),
        type: cred.type,
        response: {
            authenticatorData: bufToB64u(r.authenticatorData),
            clientDataJSON: bufToB64u(r.clientDataJSON),
            signature: bufToB64u(r.signature),
            userHandle: r.userHandle ? bufToB64u(r.userHandle) : null,
        },
        clientExtensionResults: cred.getClientExtensionResults
            ? cred.getClientExtensionResults()
            : {},
    };
}

export function isWebAuthnSupported() {
    return (
        typeof window !== "undefined" &&
        "PublicKeyCredential" in window &&
        typeof navigator?.credentials?.create === "function"
    );
}

export async function registerPasskey() {
    if (!isWebAuthnSupported()) {
        throw new Error("Your browser does not support passkeys.");
    }
    const begin = await api.post("/security/passkey/register/begin");
    const optsJson = JSON.parse(begin.data.options);
    const cred = await navigator.credentials.create({
        publicKey: decodeRegistrationOptions(optsJson),
    });
    if (!cred) throw new Error("Registration was cancelled.");
    const payload = encodeRegistrationCredential(cred);
    const resp = await api.post("/security/passkey/register/complete", {
        credential: payload,
        challenge: optsJson.challenge,
    });
    return resp.data;
}

export async function verifyPasskey() {
    if (!isWebAuthnSupported()) {
        throw new Error("Your browser does not support passkeys.");
    }
    const begin = await api.post("/security/passkey/verify/begin");
    const optsJson = JSON.parse(begin.data.options);
    const cred = await navigator.credentials.get({
        publicKey: decodeAuthOptions(optsJson),
    });
    if (!cred) throw new Error("Authentication was cancelled.");
    const payload = encodeAuthCredential(cred);
    const resp = await api.post("/security/passkey/verify/complete", {
        credential: payload,
        challenge: optsJson.challenge,
    });
    return resp.data;
}
